import {MAX_EXAMPLE_VOICE} from '../defines/protocol';
import {IsEmptyString, IsEmptyVar, IsEqualVoiceFile, Txt2Date} from '../defines/common.func';
import {DERIVATIVE_SEPARATOR, JANGDAN, POOMSA, WORD_SEPARATOR_LINE, WORD_SEPARATOR_NUM} from '../defines/gukeo.const';
import {GetHanjaHun, GetJangDan, GetPoomSa, GetSeungJo} from '../defines/gukeo.func';
import {SimpleWord} from './simple-word.model';
import {SimpleDialect} from './dialect.model';


export type ExampleList = Array<Array<string | null> | null>;

export type ExampleSJ = {
  seungjo: string | null;
  example: string;
  highlight: string | null;  // 예문 글자 강조 마스크 (글자별 0:없음, 1:highlight, 2:highlight2). example와 글자 단위로 정렬됨.
  voiceExIdx: number | null;  // 음성 파일 인덱스. null이면 없음.
}

export type ExampleSJList = Array<Array<ExampleSJ | null> | null>;

export type LinkedExample = {
  id: string;
  example_s: string | null;
  example_t: string;
  example_highlight: string | null;
  example_voice: string | null;
};


function getHomonym(simpleWordList: Array<object>): Array<SimpleWord> {
  const list = new Array<SimpleWord>();
  simpleWordList.forEach(obj => {
    list.push(new SimpleWord(obj));
  });
  return list.length > 0 ? list : null;
}

function getDialect(simpleDialectList: Array<object>): Array<SimpleDialect> {
  const list = new Array<SimpleDialect>();
  simpleDialectList.forEach(obj => list.push(new SimpleDialect(obj)));
  return list.length > 0 ? list : null;
}

function getLinkedExamples(exampleList: Array<any>): Array<LinkedExample> {
  const list = new Array<LinkedExample>();

  exampleList.forEach(obj => {
    const id = obj?.id ?? null;
    const example_s = obj?.example_s ?? null;
    const example_t = obj?.example_t ?? null;
    const example_highlight = obj?.example_highlight ?? null;
    const example_voice = obj?.example_voice ?? null;

    if (!IsEmptyString(id) && !IsEmptyString(example_t)) {
      list.push({
        id: String(id),
        example_s: IsEmptyString(example_s) ? null : String(example_s),
        example_t: String(example_t),
        example_highlight: IsEmptyString(example_highlight) ? null : String(example_highlight),
        example_voice: IsEmptyString(example_voice) ? null : String(example_voice),
      });
    }
  });

  return list.length > 0 ? list : null;
}


export class Word extends SimpleWord {
  rhythm: Array<JANGDAN>;
  hanjaHun: Array<string>;
  partOfSpeech: Array<POOMSA>;
  homonyms: Array<SimpleWord>;
  dialects: Array<SimpleDialect>;
  derivative: Array<string>;
  synonym: Array<string>;
  meaning3: Array<string>;
  example3: ExampleList;
  example_sj: ExampleSJList | null;  // 예문 - 성조만
  examples: Array<LinkedExample> | null;  // 문장 듣기 연결 예문

  exampleVoice: Array<string>;
  registered: Date;
  lastMod: Date;
  lastOp: string;

  bookmark: boolean;

  constructor(obj?: any) {
    super(obj);

    this.rhythm             = (obj && obj.rhythm)             ? GetJangDan(obj.rhythm) : null;
    this.hanjaHun           = (obj && obj.hanja_hun)          ? GetHanjaHun(obj.hanja_hun) : null;
    this.partOfSpeech       = (obj && obj.part_of_speech)     ? GetPoomSa(obj.part_of_speech) : null;
    this.homonyms           = (obj && obj.homonyms)           ? getHomonym(obj.homonyms) : null;
    this.dialects           = (obj && obj.dialects)           ? getDialect(obj.dialects) : null;
    this.derivative         = (obj && obj.derivative3)        ? this.makeDerivative(obj.derivative3) : null;
    this.synonym            = (obj && obj.synonym3)           ? this.makeDerivative(obj.synonym3) : null;
    this.meaning3           = (obj && obj.meaning3)           ? String(obj.meaning3).split(WORD_SEPARATOR_NUM) : null;
    this.example3           = (obj && obj.example3)           ? this.parseExample3(obj.example3) : null;

    this.exampleVoice       = (obj && obj.example_voices)     ?? null;
    this.example_sj         = (obj && obj.example_t && obj.example_s) ? this.parseExampleSJ(obj.example_s, obj.example_t, obj.example_highlight ?? null) : null;
    this.examples           = (obj && Array.isArray(obj.examples)) ? getLinkedExamples(obj.examples) : null;

    this.registered         = (obj && obj.registered)         ? Txt2Date(obj.registered) : null;
    this.lastMod            = (obj && obj.last_mod)           ? Txt2Date(obj.last_mod) : null;
    this.lastOp             = (obj && obj.last_op)            ?? null;

    this.bookmark           = (obj && !IsEmptyVar(obj.bookmark)) ? !!obj.bookmark : false;

    // console.log('Word =', this, obj);
  }


  public GetMeaning3Nth(nth: number): string | null {
    const meaning = this.meaning3?.[nth] ?? null;
    return IsEmptyString(meaning) ? null : meaning;
  }

  /**
   * 예문 배열 가지고 오는 함수.
   * (규칙이 복잡함)
   * 뜻이 하나 있는 경우: 첫번째 예문 사용
   * 뜻이 두개 이상 있는 경우 (= 기본 뜻이 있는 경우): 기본 뜻에는 예문이 없다. 그래서 (예문 갯수 === 뜻 갯수 - 1)
   * @param nth
   * @constructor
   * @protected
   */
  public GetExample3Nth(nth: number): Array<string | null> | null {
    if (this.exampleLen > 1) {
      // 뜻이 두개 이상인 경우
      // 뜻 0번은 기본 뜻이라서 예문이 없다.
      // 뜻 1번부터 예문이 있어서 -1을 해줌.
      return nth === 0 ? null : this.example3?.[nth - 1] ?? null;
    }
    else {
      return this.example3?.[nth] ?? null;
    }
  }

  public GetExampleSjNth(nth: number): Array<ExampleSJ | null> | null {
    if (this.exampleLen > 1) {
      // 뜻이 두개 이상인 경우
      // 뜻 0번은 기본 뜻이라서 예문이 없다.
      // 뜻 1번부터 예문이 있어서 -1을 해줌.
      return nth === 0 ? null : this.example_sj?.[nth - 1] ?? null;
    }
    else {
      return this.example_sj?.[nth] ?? null;
    }
  }

  /**
   * 특정 예문 번호와 문장 번호에 해당하는 ExampleSJ 객체를 반환합니다.
   * @param nth - 예문 번호
   * @param ex - 예문 번호 안의 문장 번호
   * @constructor
   */
  public GetExampleSjNth2(nth: number, ex: number): ExampleSJ | null {
    const exampleSj = this.GetExampleSjNth(nth);
    if (!exampleSj) return null;

    return exampleSj[ex];
  }

  /**
   * 예문 번호와 문장 번호에 해당하는 음성 데이터를 반환합니다.
   * @param num - 예문 번호 (1부터 시작)
   * @param idx - 문장 번호 (0부터 시작)
   * @constructor
   */
  public GetExVoice(num: number, idx: number): string | null {
    if (num == 0) ++num;  // 예문 번호가 0인 경우 예문은 1번을 사용한다.

    const voiceFile = this.id + '-' + num + '-' + (idx + 1);  // idx + 1: 예문 파일명은 1부터 시작

    const matchFile = this.exampleVoice?.find(v => IsEqualVoiceFile(v, voiceFile)) ?? null;
    return matchFile;
  }

  public GetExVoiceIdx(num: number, idx: number): number {
    if (num == 0) ++num;  // 예문 번호가 0인 경우 예문은 1번을 사용한다.

    const voiceFile = this.id + '-' + num + '-' + (idx + 1);  // idx + 1: 예문 파일명은 1부터 시작

    const matchIdx = this.exampleVoice?.findIndex(v => IsEqualVoiceFile(v, voiceFile)) ?? null;
    return matchIdx;
  }


  /**
   * 예문을 파싱하는 함수
   *
   * NOTE: /를 줄넘김 기호로 사용하는데 html tag 안에 있으면 줄넘김 기호로 인식하지 않아야 함.
   *
   * @param example3 - 예문 데이터
   * @returns - 예문 데이터
   */
  private parseExample3(example3: string): ExampleList | null {
    if (!example3) return null;

    return example3.split(WORD_SEPARATOR_NUM).map(example =>
      !IsEmptyString(example) ?
        example.split(/(?![^<]*>)\//)
          .map(ex => !IsEmptyString(ex) ? ex : null)
          .filter(ex => ex !== null)
        : null
    );
  }

  /**
   * 성조 포함한 예문을 파싱하는 함수
   * @param example_s - 예문의 성조 데이터
   * @param example_t - 예문의 문장 데이터
   * @private
   */
    private parseExampleSJ(example_s: string, example_t: string, example_highlight: string | null): ExampleSJList | null {
      if (!example_s || !example_t) return null;

      const seungjos = example_s.split(WORD_SEPARATOR_NUM).map(seungjo =>
        !IsEmptyString(seungjo) ? seungjo.split(WORD_SEPARATOR_LINE).map(sj => !IsEmptyString(sj) ? sj : null) : null
      );
      const examples = example_t.split(WORD_SEPARATOR_NUM).map(example =>
        !IsEmptyString(example) ? example.split(WORD_SEPARATOR_LINE).map(ex => !IsEmptyString(ex) ? ex : null) : null
      );
      const highlights = this.parseExampleHighlight(example_highlight, example_t);

      return examples.map((example, num) => {
        // num = 예문 번호
        if (!example) return null;

        const midx = this.exampleLen > 1 ? num + 1 : num;  // 뜻 번호 계산

        return example
          .filter(ex => ex !== null) // null인 항목을 제거합니다.
          .map((ex, i) => ex ? {
            // i = 예문 내의 문장 번호
            seungjo: seungjos[num]?.[i] ?? null,
            example: ex,
            highlight: highlights[num]?.[i] ?? null,
            voiceExIdx: (() => {
              const idx = this.GetExVoiceIdx(midx, i);
              return idx === -1 ? null : idx;
            })(),
          } : null
        );
      });
    }

  /**
   * 예문 강조 마스크(example_highlight)를 example_t와 동일한 묶음/문장 구조로 분해한다.
   *
   * 마스크는 example_t와 글자 단위로 1:1 정렬되며 '/'(문장 구분) 위치도 한 글자(0)로 포함되므로,
   * 성조(example_s)처럼 '/'로 split하지 않고 example_t 각 문장의 길이에 맞춰 위치 기반으로 잘라낸다.
   * 마스크가 없거나(미배포·빈 값) 묶음 길이가 어긋나면 해당 묶음을 강조 없음(null)으로 처리한다.
   *
   * @param example_highlight - 예문 강조 마스크 문자열
   * @param example_t - 예문 텍스트 (정렬 기준)
   * @private
   */
  private parseExampleHighlight(example_highlight: string | null, example_t: string): Array<Array<string | null> | null> {
    if (IsEmptyString(example_highlight)) return [];

    const highlightBundles = example_highlight.split(WORD_SEPARATOR_NUM);
    return example_t.split(WORD_SEPARATOR_NUM).map((exBundle, b) => {
      if (IsEmptyString(exBundle)) return null;

      const sentences = exBundle.split(WORD_SEPARATOR_LINE);
      const hlBundle = highlightBundles[b];
      // 마스크 누락 또는 묶음 길이 불일치 → 강조 없음 (회귀 방지)
      if (IsEmptyString(hlBundle) || hlBundle.length !== exBundle.length) {
        return sentences.map(() => null);
      }

      const pieces: Array<string | null> = [];
      let offset = 0;
      for (const sentence of sentences) {
        pieces.push(!IsEmptyString(sentence) ? hlBundle.slice(offset, offset + sentence.length) : null);
        offset += sentence.length + 1;  // +1: '/'(WORD_SEPARATOR_LINE) 한 글자만큼 건너뜀
      }
      return pieces;
    });
  }

  public get hasDefaultMeaning(): boolean {
    return !IsEmptyString(this.meaning3?.[0]);
  }

  public get derivativeTxt(): string {
    return IsEmptyVar(this.derivative) ? '' : this.derivative.join(', ');
  }

  public get synonymTxt(): string {
    return IsEmptyVar(this.synonym) ? '' : this.synonym.join(', ');
  }

  public HaveExVoiceNth(n: number): boolean {
    return (
      this.exampleVoice &&
      this.exampleVoice.length > n &&
      !IsEmptyString(this.exampleVoice[n]));
  }

  private get exampleLen(): number {
    return this.meaning3?.length ?? 0;
  }

  private makeDerivative(txt: string): Array<string> {
    return txt.split(DERIVATIVE_SEPARATOR).map(item => item.trim());
  }
}
